import {
  Column,
  Container,
  EmptyScreen,
  EmptyState,
  Row
} from '@cdab/scania/qpr/components'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import type { ExpiringDeviation } from '@cdab/scania/qpr/schema'
import {
  filterTableData,
  sortTableData,
  type SortConfig
} from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter } from '@cdab/utils'
import {
  TdsBodyCell,
  TdsHeaderCell,
  TdsIcon,
  TdsTable,
  TdsTableBody,
  TdsTableBodyRow,
  TdsTableFooter,
  TdsTableHeader,
  TdsTableToolbar
} from '@scania/tegel-react'
import type { TFunction } from 'i18next'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  isRouteErrorResponse,
  useNavigate,
  useRouteError,
  useRouteLoaderData
} from 'react-router-dom'
import type { DashboardLoaderData } from './dashboard'
import { StyledColumn } from './dashboard'

type PreparedExpiringDeviationData = {
  auditDate: string
  dealerCity: string
  dealerName: string
  expiryDate: string
  hasDeviations: string
  auditId: number
}

export function ExpiringActionPlans() {
  const { expiringDeviations } = useRouteLoaderData(
    'dashboard'
  ) as DashboardLoaderData
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const [sortConfig, setSortConfig] = useState<SortConfig>(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [processedData, setProcessedData] = useState<
    PreparedExpiringDeviationData[]
  >([])

  const handleTableSort = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        columnKey: string | undefined
        sortingDirection: 'asc' | 'desc'
      }>
    ) => {
      if (!event.detail.columnKey) {
        setSortConfig(null)
        return
      }
      const { columnKey, sortingDirection } = event.detail
      setSortConfig({ columnKey, sortingDirection })
    },
    []
  )

  const handleTableFilter = useCallback(
    (
      event: CustomEvent<{
        tableId: string | undefined
        query: string | undefined
      }>
    ) => {
      if (!event.detail.query) {
        setFilterQuery('')
        return
      }
      const { query } = event.detail
      setFilterQuery(query)
    },
    []
  )

  const handleRowClick = (auditId: number) => {
    if (auditId) {
      navigate(`/audit/${auditId}/deviations`)
    }
  }

  function prepareExpiringDeviationsTableData(
    data: readonly ExpiringDeviation[],
    t: TFunction
  ): PreparedExpiringDeviationData[] {
    const preparedData = data.map(expiringDeviation => {
      return {
        auditDate: expiringDeviation.auditDate.toISOString().substring(0, 10),
        dealerCity: expiringDeviation.dealerCity,
        dealerName: expiringDeviation.dealerName,
        expiryDate: expiringDeviation.expiryDate.toISOString().substring(0, 10),
        hasDeviations: expiringDeviation.hasDeviations ? t('yes') : t('no'),
        auditId: expiringDeviation.auditId
      }
    })

    return preparedData
  }

  useEffect(() => {
    let data = prepareExpiringDeviationsTableData(expiringDeviations, t)

    if (filterQuery) {
      data = filterTableData(data, filterQuery)
    }

    if (sortConfig) {
      data = sortTableData(data, sortConfig)
    }

    setProcessedData(data)
  }, [expiringDeviations, sortConfig, filterQuery])

  return expiringDeviations.length > 0 ? (
    <StyledColumn width={12} fullHeight scrollY scrollX>
      <TdsTable responsive={true} modeVariant='secondary'>
        <TdsTableToolbar
          tableTitle={t('expiring-action-plans', { ns: 'dashboard' })}
          filter
          onTdsFilter={handleTableFilter}
        />
        <TdsTableHeader>
          <TdsHeaderCell
            onTdsSort={handleTableSort}
            cellKey='auditDate'
            cellValue={t('audit-date')}
            sortable
          />
          <TdsHeaderCell
            onTdsSort={handleTableSort}
            cellKey='dealerCity'
            cellValue={t('city')}
            sortable
          />
          <TdsHeaderCell
            onTdsSort={handleTableSort}
            cellKey='dealerName'
            cellValue={capitalizeFirstLetter(t('dealer'))}
            sortable
          />
          <TdsHeaderCell
            onTdsSort={handleTableSort}
            cellKey='expiryDate'
            cellValue={t('expiry-date')}
            sortable
          />
          <TdsHeaderCell
            onTdsSort={handleTableSort}
            cellKey='hasDeviations'
            cellValue={t('has-deviations')}
            sortable
          />
        </TdsTableHeader>
        <TdsTableBody>
          {processedData.map((deviation, index) => (
            <TdsTableBodyRow
              key={deviation.auditId || index}
              onClick={() => handleRowClick(deviation.auditId)}
            >
              <TdsBodyCell
                cellKey='auditDate'
                cellValue={deviation.auditDate}
              />
              <TdsBodyCell
                cellKey='dealerCity'
                cellValue={deviation.dealerCity}
              />
              <TdsBodyCell
                cellKey='dealerName'
                cellValue={deviation.dealerName}
              />
              <TdsBodyCell
                cellKey='expiryDate'
                cellValue={deviation.expiryDate}
              />
              <TdsBodyCell
                cellKey='hasDeviations'
                cellValue={deviation.hasDeviations}
              />
            </TdsTableBodyRow>
          ))}
        </TdsTableBody>
        <TdsTableFooter pagination />
      </TdsTable>
    </StyledColumn>
  ) : (
    <EmptyScreen.CenteredWrapper>
      <EmptyScreen
        icon={<TdsIcon name='message_inactive' size='lg' />}
        title={t('no-expiring-deviations-found', { ns: 'common' })}
        description={t('everything-under-control', { ns: 'common' })}
      />
    </EmptyScreen.CenteredWrapper>
  )
}

export function ErrorBoundary() {
  const { t } = useTranslation('errors')
  const isLg = useCssVariableBreakpoint('--tds-grid-lg')
  const error = useRouteError()

  if (isRouteErrorResponse(error)) {
    return (
      <Container
        fluid='normal'
        paddingOnColumns={!isLg}
        paddingOnContainer={!isLg}
      >
        <Row>
          <Column width={12} padding={isLg} scrollX scrollY>
            <EmptyState
              title={t('something-bad')}
              description={t('talk-to-admin')}
            />
          </Column>
        </Row>
      </Container>
    )
  }

  throw error
}
